import { Cifra, Cifras } from '@/components/ui/cifras';
import { EstadoError } from '@/components/ui/estado-vacio';
import { Skeleton } from '@/components/ui/skeleton';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { formatoCantidad, formatoNumero } from '@/lib/utils/formato-precio';
import { usePedidoConEspera } from '@/features/pricing-rules/lib/use-pedido-con-espera';
import { usePermisosDeReglas } from '@/features/pricing-rules/lib/use-permisos-de-reglas';
import { hrefDeLaEmpresa } from '@/router/href-de-la-empresa';
import { hrefsDeCompite } from '@/features/compite/lib/rutas';
import { strategyApplicationsApi } from '../services/strategy-applications-api';
import type {
  EstrategiaEnBorradorBody,
  SimulacionDeEstrategia,
} from '../types/strategy-applications.types';

/**
 * Las reglas que alcanzan a la estrategia y «si hoy corriera» (maqueta S5). Es la
 * estrategia EN BORRADOR simulada por el backend (4B-datos T10) con la misma
 * cuenta que la corrida: nada se crea. La estrategia no tiene reglas propias.
 */

const num = formatoCantidad;

export function efectoDeRegla(r: SimulacionDeEstrategia['reglas'][number]): string {
  const sinDato =
    r.sinDato > 0 ? `a ${num(r.sinDato)} no los puede medir: les falta un dato` : null;
  if (r.alcanza > 0 && r.limita === 0 && r.enElChoque === 0) {
    // Re-auditoría 26-sep: si no pudo medir a ninguno (p. ej. margen sin costo),
    // es «no sé», nunca «ya lo cumple».
    if (r.sinDato >= r.alcanza) return sinDato!;
    // 4B-datos T10 (pendiente cruzado 5): el backend dice cuál la tapa.
    const base = r.cubiertaPor
      ? `hoy no limita a ninguno: la cubre «${r.cubiertaPor.ruleName}», que es más exigente`
      : 'hoy no limita a ninguno: el precio que pide la estrategia ya lo cumple';
    return sinDato ? `${base} · ${sinDato}` : base;
  }
  const partes: string[] = [];
  if (r.limita > 0) {
    if (r.tipos.includes('MOVIMIENTO'))
      partes.push(
        r.limita === 1
          ? '1 necesita más de una corrida para llegar'
          : `${num(r.limita)} necesitan más de una corrida para llegar`
      );
    else if (r.tipos.includes('MARGEN_MINIMO'))
      partes.push(
        r.limita === 1
          ? '1 no puede llegar a lo que pide: queda en el margen'
          : `${num(r.limita)} no pueden llegar a lo que pide: quedan en el margen`
      );
    else partes.push(`limita a ${num(r.limita)}`);
  }
  if (r.enElChoque > 0) partes.push(`${num(r.enElChoque)} quedan en conflicto entre reglas`);
  if (sinDato) partes.push(sinDato);
  return partes.join(' · ');
}

/**
 * Lote A del 2-oct (A3): que «si hoy corriera» no cuenta a los que ya tienen
 * otra estrategia mientras no se elija lo dice el aviso de esos productos (paso
 * 3, el que tiene «Ver cuáles»), no una línea suelta acá.
 */
export function ReglasQueLaAlcanzan({ body }: { body: EstrategiaEnBorradorBody | null }) {
  const pedido = usePedidoConEspera(body ? JSON.stringify(body) : null, () =>
    strategyApplicationsApi.simular(body!)
  );
  // El enlace a Reglas sólo con su permiso (re-auditoría 26-sep): sin él, la
  // página muestra el cartel de módulo cerrado.
  const permisosReglas = usePermisosDeReglas();

  if (!body) return null;
  if (pedido.estado === 'ERROR') {
    return (
      <EstadoError onReintentar={pedido.reintentar}>
        {mensajeDeError(pedido.error, 'No se pudo calcular qué harían las reglas.')}
      </EstadoError>
    );
  }
  if (pedido.estado !== 'LISTO') {
    // La carga con la forma de lo que va a aparecer (mapa 226, hallazgo 8).
    return (
      <div className="space-y-2" aria-label="Calculando qué harían las reglas">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }
  const s = pedido.data;
  const h = s.siHoyCorriera;
  const esperan = h.superanUmbral + h.tePregunta + h.quedanIgualPorUnaRegla;
  const notas = [
    s.sinDatos > 0
      ? s.sinDatos === 1
        ? '1 no se puede calcular'
        : `${num(s.sinDatos)} no se pueden calcular`
      : null,
    h.quedanIgualPorUnaRegla > 0
      ? h.quedanIgualPorUnaRegla === 1
        ? 'a 1 una regla lo deja como está: te espera en Por decidir'
        : `a ${num(h.quedanIgualPorUnaRegla)} una regla los deja como están: te esperan en Por decidir`
      : null,
    s.necesitanDosCorridas > 0
      ? s.necesitanDosCorridas === 1
        ? '1 necesita más de una corrida para llegar'
        : `${num(s.necesitanDosCorridas)} necesitan más de una corrida para llegar`
      : null,
  ].filter((x): x is string => x != null);
  return (
    <div className="space-y-3" data-testid="reglas-que-la-alcanzan">
      <div>
        <div className="text-sm font-medium">
          Las reglas que tocan sus productos{' '}
          {permisosReglas.ver && (
            <a
              href={hrefDeLaEmpresa(hrefsDeCompite.reglas())}
              className="text-xs font-normal text-primary underline-offset-4 hover:underline"
            >
              {permisosReglas.cambiaAlgo ? 'se cambian en Reglas' : 'se ven en Reglas'}
            </a>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          La estrategia no tiene reglas propias: usa las que alcanzan a sus productos. Acá se ven,
          con lo que le hacen.
        </p>
      </div>
      {s.reglas.length === 0 ? (
        <p className="text-sm text-muted-foreground">Ninguna regla alcanza a estos productos.</p>
      ) : (
        <ul className="divide-y rounded-md border text-sm">
          {s.reglas.map((r) => {
            const apagada = r.alcanza > 0 && r.limita === 0 && r.enElChoque === 0;
            return (
              <li key={r.ruleId} className="px-3 py-2">
                <span className="font-semibold">{r.ruleName}</span>{' '}
                <span className={apagada ? 'text-muted-foreground' : 'text-foreground/80'}>
                  · alcanza a {num(r.alcanza)} de {num(s.evaluados)} · {efectoDeRegla(r)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <div className="space-y-1.5">
        <div data-testid="si-hoy-corriera" className="text-xs text-muted-foreground">
          Si hoy corriera · {num(s.evaluados)} de {num(s.productos)} se pueden calcular
        </div>
        {/* Sólo se colorea la cifra que pide atención (maqueta del pulido). */}
        <Cifras>
          {/* Con 1, en singular (auditoría M3). */}
          <Cifra
            chica
            valor={h.seAplicanSolos}
            rotulo={h.seAplicanSolos === 1 ? 'se aplica solo' : 'se aplican solos'}
          />
          {/* Re-auditoría 26-sep: los que una regla deja como están también esperan
              (la corrida los deja pendientes en Por decidir, motivo «limitado»). */}
          <Cifra
            chica
            data-testid="si-hoy-te-esperan"
            tono={esperan > 0 ? 'alerta' : 'neutro'}
            valor={esperan}
            rotulo={
              body.mode === 'SUPERVISED'
                ? `${esperan === 1 ? 'te espera' : 'te esperan'}: pediste que te pregunte siempre`
                : body.askAboveChangePct != null
                  ? esperan === 1
                    ? `te espera: cambia más de ${formatoNumero(body.askAboveChangePct)} % o una regla lo deja como está`
                    : `te esperan: cambian más de ${formatoNumero(body.askAboveChangePct)} % o una regla los deja como están`
                  : esperan === 1
                    ? 'te espera: una regla lo deja como está'
                    : 'te esperan: una regla los deja como están'
            }
          />
          <Cifra
            chica
            tono={h.sinPrecioPosible > 0 ? 'bloqueo' : 'neutro'}
            valor={h.sinPrecioPosible}
            rotulo="en conflicto entre reglas"
          />
          <Cifra
            chica
            data-testid="si-hoy-ya-estan-bien"
            valor={h.yaEstanBien}
            rotulo={h.yaEstanBien === 1 ? 'ya está bien' : 'ya están bien'}
          />
        </Cifras>
        {notas.length > 0 && <p className="text-xs text-muted-foreground">{notas.join(' · ')}</p>}
      </div>
    </div>
  );
}
